/* ============================================================================
   Mercurial Tones — REACT preview deck (real Serum 2 renders, Web Audio)
   Contract (the page must provide these):
     window.PRESETS = [{num,name,label,cls:'lead'|'bass',url}]  // url = mp3 asset
     DOM ids: #plist #ppBtn #vol #nowName #nowMeta #nowState #audioWarn #viz
   The engine injects .prow rows into #plist. Themes style:
     .prow .idx .pn .pt .typ .eqmini  + states .prow.active .prow.playing
   Visualiser colors read from CSS vars --viz-a / --viz-b on #viz (fallback green).
   Playback: fetch + decodeAudioData into looping AudioBufferSource -> gain ->
   analyser. Falls back to a plain looping <audio> element if decode/CORS fails.
   ============================================================================ */
(() => {
  const PRESETS = window.PRESETS || [];
  const $ = id => document.getElementById(id);
  const plist=$('plist'), ppBtn=$('ppBtn'), volEl=$('vol'),
        nowName=$('nowName'), nowMeta=$('nowMeta'), nowState=$('nowState'),
        warn=$('audioWarn'), canvas=$('viz');
  if(!plist || !canvas) return;
  const cx = canvas.getContext('2d');

  // viz colors from theme
  const css = getComputedStyle(canvas);
  const vizA = (css.getPropertyValue('--viz-a')||'#88ff2d').trim();
  const vizB = (css.getPropertyValue('--viz-b')||'#b9ff77').trim();
  const vizLine = (css.getPropertyValue('--viz-line')||'#243021').trim();

  // build rows
  PRESETS.forEach((p,i)=>{
    const row=document.createElement('div');
    row.className='prow '+p.cls; row.dataset.i=i;
    row.innerHTML=`<div class="idx"><span>${p.num}</span><div class="eqmini"><i></i><i></i><i></i></div></div>
      <div class="meta"><div class="pn">${p.name}</div><div class="pt">PATCH ${p.num} · ${p.label}</div></div>
      <div class="typ">${p.label}</div>`;
    row.addEventListener('click',()=>toggle(i));
    plist.appendChild(row);
  });

  let ctx,master,analyser,freqData;
  let playing=false,loading=false,current=-1,src=null,fallbackEl=null,loadSeq=0;
  const buffers={};

  function ensureCtx(){
    if(ctx) return true;
    try{
      ctx=new (window.AudioContext||window.webkitAudioContext)();
      master=ctx.createGain(); master.gain.value=volEl?volEl.value/100:0.62;
      analyser=ctx.createAnalyser(); analyser.fftSize=256; freqData=new Uint8Array(analyser.frequencyBinCount);
      master.connect(analyser); analyser.connect(ctx.destination);
      return true;
    }catch(e){ return false; }
  }
  function stopSource(){
    if(src){ try{src.stop();}catch(e){} try{src.disconnect();}catch(e){} src=null; }
    if(fallbackEl){ try{fallbackEl.pause();}catch(e){} fallbackEl=null; }
  }
  function playBuffer(buf){
    stopSource();
    src=ctx.createBufferSource(); src.buffer=buf; src.loop=true;
    src.connect(master); src.start();
  }
  function playFallback(url){
    stopSource();
    fallbackEl=new Audio(url); fallbackEl.loop=true;
    fallbackEl.volume=volEl?volEl.value/100:0.62;
    fallbackEl.play().catch(()=>{ if(warn) warn.style.display='block'; });
  }
  function start(i){
    if(!ensureCtx()){ // no Web Audio at all -> plain <audio>
      current=i; playing=true; playFallback(PRESETS[i].url); syncUI(); return;
    }
    if(ctx.state==='suspended') ctx.resume();
    current=i; playing=true; const seq=++loadSeq;
    if(buffers[i]){ playBuffer(buffers[i]); loading=false; syncUI(); paint(); return; }
    loading=true; syncUI();
    fetch(PRESETS[i].url)
      .then(r=>{ if(!r.ok) throw new Error('http '+r.status); return r.arrayBuffer(); })
      .then(ab=>new Promise((res,rej)=>ctx.decodeAudioData(ab,res,rej)))
      .then(buf=>{
        buffers[i]=buf;
        if(seq!==loadSeq||!playing||current!==i) return; // superseded
        loading=false; playBuffer(buf); syncUI(); paint();
      })
      .catch(()=>{
        if(seq!==loadSeq||!playing||current!==i) return;
        loading=false; playFallback(PRESETS[i].url); syncUI(); paint();
      });
  }
  function stopAudio(u=true){ playing=false; loading=false; stopSource(); if(u) syncUI(); }
  function toggle(i){ (playing&&current===i)?stopAudio():start(i); }
  ppBtn && ppBtn.addEventListener('click',()=>{ playing?stopAudio():start(current<0?0:current); });
  volEl && volEl.addEventListener('input',()=>{
    const v=volEl.value/100;
    if(master) master.gain.value=v;
    if(fallbackEl) fallbackEl.volume=v;
  });

  function syncUI(){
    if(warn) warn.style.display='none';
    document.querySelectorAll('.prow').forEach(r=>{
      const i=+r.dataset.i;
      r.classList.toggle('active',i===current);
      r.classList.toggle('playing',i===current&&playing&&!loading);
    });
    if(ppBtn) ppBtn.textContent=playing?'❚❚':'▶';
    if(nowState) nowState.textContent=loading?'loading':(playing?'live':'paused');
    if(current>=0){
      if(nowName) nowName.textContent=PRESETS[current].name;
      if(nowMeta) nowMeta.textContent=`PATCH ${PRESETS[current].num} · ${PRESETS[current].label} · SERUM 2`;
    }
  }
  function paint(){
    const W=canvas.width=canvas.clientWidth*devicePixelRatio, H=canvas.height=canvas.clientHeight*devicePixelRatio;
    cx.clearRect(0,0,W,H);
    if(analyser&&playing&&src){
      analyser.getByteFrequencyData(freqData);
      const bars=48,bw=W/bars;
      for(let b=0;b<bars;b++){
        const v=freqData[Math.floor(b/bars*freqData.length)]/255, h=Math.max(2,v*H*0.95);
        cx.fillStyle=b%7===0?vizB:vizA;
        cx.globalAlpha=0.35+v*0.65; cx.fillRect(b*bw+1,H-h,bw-2,h);
      }
      cx.globalAlpha=1;
    } else if(playing&&(fallbackEl||loading)){
      // fallback element / loading: pulse bars from time so the deck never looks dead
      const t=performance.now()/1000, bars=48,bw=W/bars;
      for(let b=0;b<bars;b++){
        const v=loading?0.08+0.05*Math.sin(t*6+b*.7):0.25+0.22*Math.abs(Math.sin(t*4.4+b*0.9))+0.1*Math.abs(Math.sin(t*9+b*2.3));
        const h=Math.max(2,v*H*0.95);
        cx.fillStyle=b%7===0?vizB:vizA;
        cx.globalAlpha=0.3+v*0.6; cx.fillRect(b*bw+1,H-h,bw-2,h);
      }
      cx.globalAlpha=1;
    } else { cx.strokeStyle=vizLine; cx.beginPath(); cx.moveTo(0,H/2); cx.lineTo(W,H/2); cx.stroke(); }
    if(playing) requestAnimationFrame(paint);
  }
  requestAnimationFrame(paint);
  window.addEventListener('resize',()=>{ if(!playing) paint(); });
})();
